import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Category, Product } from '../types';
import ProductCard from '../components/ProductCard';
import ProductTable from '../components/ProductTable';
import CategoryCard from '../components/CategoryCard';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import { fetchCategoryBySlug, fetchProducts } from '../services/api';
import { hasRealImage } from '../utils/images';

const CategoryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [category, setCategory] = useState<Category | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('rating');
  const [view, setView] = useState<'cards' | 'table'>('cards');

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      const [cat, prods] = await Promise.all([
        fetchCategoryBySlug(slug || ''),
        fetchProducts({ category: slug }),
      ]);
      if (!active) return;
      setCategory(cat);
      setProducts(prods);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [slug]);

  const sorted = [...products].sort((a, b) => {
    // Products without imagery always sink to the bottom to keep the grid tidy.
    const imgDiff = Number(hasRealImage(b)) - Number(hasRealImage(a));
    if (imgDiff !== 0) return imgDiff;
    switch (sort) {
      case 'saved': return (b.saves || 0) - (a.saves || 0);
      case 'price-low': return a.price - b.price;
      case 'price-high': return b.price - a.price;
      case 'health': return b.healthScore - a.healthScore;
      case 'name': return a.name.localeCompare(b.name);
      default: return b.rating - a.rating;
    }
  });

  if (loading) return (<div className="container-custom py-28 flex justify-center"><LoadingSpinner /></div>);

  if (!category) return (
    <div className="container-custom py-24 text-center">
      <h1 className="mb-4">Category not found</h1>
      <p className="text-earth-600 mb-8">We couldn't find that category.</p>
      <Link to="/categories" className="btn btn-primary">Browse all categories</Link>
    </div>
  );

  const ancestors = category.ancestors || [];
  const children = category.children || [];

  return (
    <div className="min-h-screen">
      <SEO title={category.name} description={category.description}
        image={`/og/category/${category.slug}.png`} url={`https://goodcart.org/categories/${category.slug}`} type="website" />

      {/* Compact header: breadcrumb + title + count in one slim band */}
      <section className="bg-sage-700 text-cream-50">
        <div className="container-custom py-6 md:py-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-sage-200 mb-2">
              <Link to="/categories" className="hover:text-cream-50">All categories</Link>
              {ancestors.map((a) => (
                <React.Fragment key={a.id}>
                  <span aria-hidden className="text-sage-300">/</span>
                  <Link to={`/categories/${a.slug}`} className="hover:text-cream-50">{a.name}</Link>
                </React.Fragment>
              ))}
              <span aria-hidden className="text-sage-300">/</span>
              <span className="text-cream-50 font-medium">{category.name}</span>
            </nav>
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-display text-cream-50">{category.name}</h1>
              <p className="text-sage-200 text-sm font-medium">
                {children.length > 0 ? `${children.length} sub-categories · ` : ''}
                {products.length} curated {products.length === 1 ? 'product' : 'products'}
              </p>
            </div>
            {category.description && (
              <p className="mt-2 max-w-2xl text-sm text-sage-100/90 line-clamp-2">{category.description}</p>
            )}
          </motion.div>
        </div>
      </section>

      <div className="container-custom py-8 md:py-10">
        {children.length > 0 && (
          <div className="mb-10">
            <h2 className="text-earth-900 mb-2 text-xl">Shop by sub-category</h2>
            <div className="organic-divider mb-6"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {children.map((c, i) => (<CategoryCard key={c.id} category={c} index={i} size="small" />))}
            </div>
          </div>
        )}

        {sorted.length === 0 ? (
          children.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="mb-3">No products yet</h3>
              <p className="text-earth-600 mb-8">Check back soon; we're always adding more.</p>
              <Link to="/categories" className="btn btn-outline">Browse other categories</Link>
            </div>
          ) : null
        ) : (
          <>
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
              <h2 className="text-earth-900 text-xl">{children.length > 0 ? 'All products' : `${sorted.length} products`}</h2>
              <div className="flex items-center gap-3">
                {/* View toggle */}
                <div className="inline-flex rounded-full border border-earth-200 bg-white p-0.5" role="group" aria-label="View">
                  <button
                    type="button"
                    onClick={() => setView('cards')}
                    aria-pressed={view === 'cards'}
                    className={`px-3.5 py-1.5 text-sm rounded-full transition-colors ${view === 'cards' ? 'bg-sage-600 text-cream-50' : 'text-earth-600 hover:text-earth-800'}`}
                  >
                    Cards
                  </button>
                  <button
                    type="button"
                    onClick={() => setView('table')}
                    aria-pressed={view === 'table'}
                    className={`px-3.5 py-1.5 text-sm rounded-full transition-colors ${view === 'table' ? 'bg-sage-600 text-cream-50' : 'text-earth-600 hover:text-earth-800'}`}
                  >
                    Table
                  </button>
                </div>
                <label className="flex items-center gap-2 text-sm text-earth-600">
                  Sort by
                  <select value={sort} onChange={(e) => setSort(e.target.value)}
                    className="rounded-full border border-earth-200 bg-white px-4 py-2 text-earth-800 focus:outline-none focus:ring-2 focus:ring-sage-500">
                    <option value="rating">Highest rated</option>
                    <option value="saved">Most saved</option>
                    <option value="health">Health score</option>
                    <option value="price-low">Price: low to high</option>
                    <option value="price-high">Price: high to low</option>
                    <option value="name">Name</option>
                  </select>
                </label>
              </div>
            </div>
            {view === 'cards' ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {sorted.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
              </div>
            ) : (
              <ProductTable products={sorted} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
